import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { cn } from '../ui';
import { usePrefersReducedMotion } from '../../lib/motion';
import { ChatMarkdown } from './markdown';
import { useTypewriter } from './use-typewriter';
import { ActionCard, TOOL_LABEL } from './action-card';
import { ToolResultCards } from './tool-cards';
import type { Msg } from './use-assistant-chat';

/**
 * The message-turn renderer shared by the Asistente IA slide-over (drawer.tsx)
 * and the full-page Agente surface (pages/Agente.tsx). Extracting it here means
 * the two surfaces render the same transcript identically and cannot drift: the
 * rich AI turn (ChatMarkdown + ToolResultCards + confirm ActionCards) and the
 * thinking indicator live in ONE place.
 *
 * Demo polish: the NEWEST AI turn types out word-by-word (blinking maroon caret)
 * and its rich cards fade in once the text lands, so a one-shot demo answer reads
 * as if it streamed. Restored/older turns render whole. Everything gates on
 * prefers-reduced-motion (usePrefersReducedMotion) → final state, no motion.
 *
 * Only the user bubble differs by `variant`, and only cosmetically: the drawer
 * keeps its solid-maroon chat bubble (no visual delta after the refactor); the
 * page uses the demo-reel styling — a primary-tinted bubble with a small mono
 * sender label (the user's first name).
 */

type ThreadVariant = 'drawer' | 'page';

/** Short work labels rotated under the "Pensando…" chip so the gap feels like real work. */
const THINKING_PHASES = [
  'Consultando expedientes…',
  'Cotejando listas…',
  'Puntuando riesgo…',
  'Revisando documentos…',
] as const;

/** Rotate a phase label every ~700ms while thinking; static under reduced motion / retries. */
function useThinkingLabel(thinking: boolean, retryCount: number, reduced: boolean): string {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!thinking || reduced) {
      setI(0);
      return;
    }
    const id = setInterval(() => setI((n) => (n + 1) % THINKING_PHASES.length), 700);
    return () => clearInterval(id);
  }, [thinking, reduced]);
  if (retryCount > 0) return 'Reintentando…';
  if (reduced) return 'Pensando…';
  return THINKING_PHASES[i] ?? 'Pensando…';
}

/** User turn — right-aligned. Page adds a mono sender label above a tinted bubble. */
function UserBubble({
  text,
  variant,
  senderName,
}: {
  text: string;
  variant: ThreadVariant;
  senderName?: string | undefined;
}) {
  if (variant === 'page') {
    return (
      <div className="flex max-w-[85%] flex-col items-end">
        <span className="mb-1 mr-1 font-mono text-[10px] uppercase tracking-wider text-primary/70">
          {senderName || 'Tú'}
        </span>
        <div className="whitespace-pre-line break-words rounded-2xl rounded-br-md border border-primary/20 bg-primary/[0.08] px-4 py-3 text-sm font-medium text-ink">
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-[85%] whitespace-pre-line break-words rounded-2xl bg-primary px-4 py-3 text-sm text-primary-foreground">
      {text}
    </div>
  );
}

/** AI turn — identical across surfaces: markdown answer, answer cards, tool pills.
 * `animate` is true only for the just-arrived turn (drives typewriter + card fade
 * + the ScreeningCard "just-run" sweep); it's always false under reduced motion. */
function AiTurn({
  m,
  variant,
  animate,
  onSeedComposer,
}: {
  m: Msg;
  variant: ThreadVariant;
  animate: boolean;
  onSeedComposer?: ((prompt: string) => void) | undefined;
}) {
  const typing = animate && !m.error;
  const { shown, done } = useTypewriter(m.text, typing);
  const hasCards = !!m.toolResults && m.toolResults.length > 0;
  // `list_capabilities` renders its own self-evident capability card, so it never
  // needs a "tool used" chip — that would just show a raw LIST_CAPABILITIES tag.
  const chipTools = (m.tools ?? []).filter((t) => t !== 'list_capabilities');
  // Cards + chips reveal only once the text has finished typing (or immediately
  // when not animating), so the rich answer visibly "assembles".
  const revealExtras = done || !typing;
  return (
    <div
      className={cn(
        // min-w-0 lets the bubble shrink below its content's intrinsic width so
        // the markdown's own scroll containers (code blocks, wide tables) scroll
        // internally instead of blowing the bubble out horizontally.
        'min-w-0 break-words rounded-2xl px-4 py-3 text-sm',
        // Card turns claim the full width so the rich cards have room; plain
        // turns stay in a chat bubble.
        hasCards ? 'w-full max-w-full' : 'max-w-[85%]',
        m.error
          ? 'border border-tone-red/40 bg-tone-red-bg/60 text-ink'
          : 'border border-line bg-panel text-ink'
      )}
    >
      {m.error && (
        <div className="mb-2 flex items-center gap-1.5 text-tone-red">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span className="font-mono text-[10px] uppercase tracking-wider">Error</span>
        </div>
      )}
      <div className="flex flex-wrap items-end">
        <div className="min-w-0 flex-1">
          <ChatMarkdown text={typing ? shown : m.text} />
        </div>
        {typing && !done && (
          <span className="mb-1 ml-0.5 inline-block h-4 w-0.5 shrink-0 animate-pulse bg-primary/60 align-text-bottom" />
        )}
      </div>
      {revealExtras && (
        <ToolResultCards
          results={m.toolResults}
          variant={variant}
          animate={animate}
          onSeedComposer={onSeedComposer}
        />
      )}
      {revealExtras && !m.error && chipTools.length > 0 && (
        <div className={cn('mt-2 flex flex-wrap gap-1', animate && 'motion-safe:animate-slide-up')}>
          {chipTools.map((tool) => (
            <span
              key={tool}
              className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-primary-dark"
            >
              {TOOL_LABEL[tool] ?? tool}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function Thread({
  msgs,
  thinking,
  retryCount,
  variant = 'drawer',
  senderName,
  onConfirmAction,
  onCancelAction,
  onOpenEntity,
  onSeedComposer,
}: {
  msgs: Msg[];
  thinking: boolean;
  retryCount: number;
  variant?: ThreadVariant;
  /** First name shown as the sender label on the page's user bubbles. */
  senderName?: string | undefined;
  onConfirmAction: (msgIdx: number, actionIdx: number) => void;
  onCancelAction: (msgIdx: number, actionIdx: number) => void;
  onOpenEntity: (href: string) => void;
  /** Seed the surface's composer with a picked capability prompt (no auto-send).
   * Omitted by surfaces that can't fill a composer → capability cards render static. */
  onSeedComposer?: ((prompt: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const thinkingLabel = useThinkingLabel(thinking, retryCount, reduced);

  // Which turn (by ts) should animate. Set synchronously the moment `thinking`
  // flips true→false — i.e. exactly when a fresh turn lands — so there's no
  // one-frame flash. Thread-switching / reload never toggles `thinking`, so
  // restored/older turns are never animated. Stays stable until a newer turn.
  const animatingTs = useRef<number | null>(null);
  const prevThinking = useRef(thinking);
  if (prevThinking.current && !thinking) {
    const last = msgs[msgs.length - 1];
    if (last && last.role === 'ai' && !last.error && typeof last.ts === 'number') {
      animatingTs.current = last.ts;
    }
  }
  prevThinking.current = thinking;
  const animateTs = animatingTs.current;

  return (
    <>
      {msgs.map((m, i) => {
        const animate =
          !reduced && m.role === 'ai' && typeof m.ts === 'number' && m.ts === animateTs;
        return (
          <div key={i} className="space-y-2">
            <div className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
              {m.role === 'user' ? (
                <UserBubble text={m.text} variant={variant} senderName={senderName} />
              ) : (
                <AiTurn m={m} variant={variant} animate={animate} onSeedComposer={onSeedComposer} />
              )}
            </div>
            {m.role === 'ai' &&
              m.actions?.map((a, j) => (
                <div key={j} className="flex justify-start">
                  <ActionCard
                    action={a}
                    labels={{
                      proposedAction: t('assistant.proposedAction'),
                      confirm: t('assistant.confirm'),
                      cancel: t('assistant.cancel'),
                      executed: t('assistant.executed'),
                      cancelled: t('assistant.cancelled'),
                      reviewBeforeConfirm: t('assistant.reviewBeforeConfirm'),
                    }}
                    onConfirm={() => onConfirmAction(i, j)}
                    onCancel={() => onCancelAction(i, j)}
                    onOpen={onOpenEntity}
                  />
                </div>
              ))}
          </div>
        );
      })}
      {thinking && (
        <div className="flex justify-start">
          {/* Demo-reel LED think-chip: a pill (mono, bg like the tool pills) with a
              small dot filled with the AiSpark brand→teal gradient. The dot breathes
              with a subtle pulse; motion-reduce leaves it static + legible. The label
              rotates through phase-aware work states while thinking. Shared, so the
              drawer and the /agente page get the identical indicator. */}
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/10 px-3 py-1.5 font-mono text-xs text-primary-dark">
            <span
              className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full motion-reduce:animate-none"
              style={{
                backgroundImage: 'linear-gradient(135deg, #a93a4c 0%, #d98a2b 50%, #2e6e6e 100%)',
              }}
            />
            {thinkingLabel}
          </span>
        </div>
      )}
    </>
  );
}
